/**
 * "When to buy" (BLU-1506): how a supplier's blanks are bought for orders.
 *
 *   Order and wait     blanks are bought BEFORE batching: new orders add them
 *                      to the next purchase order to the supplier and wait;
 *                      when the box arrives they go to Ready to batch on their
 *                      own.
 *   Buy on print day   orders go straight to Ready to batch; the blanks are
 *                      bought the morning of the print (the just-in-time list).
 *
 * For Order and wait, three rows: Send the purchase order (every day by a set
 * time, or when you press Send order; the time is a reminder on the To buy
 * tab, nothing is sent by itself), How it's sent (you order on the supplier's
 * website and mark it sent; sending through a trade account comes later), and
 * Usually arrives in N days. That number IS the Standard lead time, which sets
 * each sent purchase order's due date; it is shown here read only and edited
 * in one place, the Lead times card below, so the two can never disagree.
 *
 * A draft with Save and Discard (DESIGN.md section 12.1). Admin only: anyone
 * else reads the chosen mode and its settings as plain text. Switching an
 * Order and wait supplier back to Buy on print day asks first (an orange,
 * reversible confirm): the next purchase order that is not sent yet is
 * emptied and its orders go to Ready to batch; a box already sent still
 * arrives.
 *
 * Follow supplier stock (BLU-1526): one more row, only for a supplier linked
 * to a catalog supplier whose stock PrintRun reads (AS Colour today). On, a
 * just-in-time blank of that catalog shows 0 in the store (its orders wait as
 * out of stock) while the supplier has fewer than the cutoff, and opens again
 * when they restock. Before it is saved the row says how many blanks it shows
 * 0 for now (one read, debounced while the cutoff is typed). While the catalog
 * list loads the row is a same-size placeholder, and if that read fails it is
 * a compact error with Retry, never a missing row. It shares the card's Save
 * and Discard; its own saved value is kept apart, so a failed save of one
 * setting never resets the other's draft.
 *
 * Follow stock works the same under either When to buy. For an Order and
 * wait supplier an out blank takes no new waits (new orders wait as out of
 * stock), and orders already on a sent purchase order keep waiting for what
 * they bought.
 */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Clock, Loader2, Save } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { NoteLine } from '@/components/shared/NoteLine';
import { SectionError } from '@/components/shared/SectionError';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from '@/hooks/use-toast';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { trpc } from '@/lib/trpc';
import { useDateFnsLocale } from '@/lib/format-dates';
import { cn } from '@/lib/utils';
import { OptionCard } from '@/features/onboarding/components/shell/OptionCard';
import { formatSendTime } from '@/features/purchase-orders/lib/order-and-wait';
import { useCatalogSuppliers } from '@/features/supplier-catalog/hooks/useSupplierCatalog';
import { supplierSupplyErrorText } from '@/features/supplier-catalog/lib/supplier-catalog-errors';

type SupplyMode = 'order_and_wait' | 'buy_on_print_day';
type SendMode = 'daily' | 'when_pressed';

/** A daily send with no time yet starts here (the mock-up's 2:00 pm). */
const DEFAULT_SEND_TIME = '14:00';
const HH_MM = /^([01][0-9]|2[0-3]):([0-5][0-9])$/;

/**
 * BLU-1526: catalog suppliers whose stock PrintRun reads every hour. Only a
 * supplier linked to one of these gets the Follow stock row.
 */
const STOCK_FOLLOWED_CATALOGS: ReadonlySet<string> = new Set(['as-colour']);
/** The cutoff a new switch starts at, and its bounds (suppliers_follow_stock_below_chk). */
export const DEFAULT_FOLLOW_CUTOFF = 10;
const FOLLOW_CUTOFF_MAX = 999_999;
/** Wait this long after the last keystroke before counting the blanks a cutoff closes. */
const PREVIEW_DEBOUNCE_MS = 300;

/**
 * The cutoff as typed, or null when it is not a whole number from 1 to
 * 999999. Full-width digits (a Chinese keyboard) read like ASCII. Empty is
 * null: an empty cutoff never becomes 0.
 */
export function parseFollowCutoff(raw: string): number | null {
  const ascii = raw.trim().replace(/[\uFF10-\uFF19]/g, (d) => String.fromCharCode(d.charCodeAt(0) - 0xfee0));
  if (!/^[0-9]{1,6}$/.test(ascii)) return null;
  const value = Number(ascii);
  return value >= 1 && value <= FOLLOW_CUTOFF_MAX ? value : null;
}

const asSupplyMode = (value: string | null | undefined): SupplyMode =>
  value === 'order_and_wait' ? 'order_and_wait' : 'buy_on_print_day';
const asSendMode = (value: string | null | undefined): SendMode => (value === 'daily' ? 'daily' : 'when_pressed');

export interface SupplierSupplyCardProps {
  supplierId: string;
  supplierName: string;
  /** The catalog supplier this supplier is linked to (null = not linked: no Follow stock row). */
  catalogSupplierId?: string | null;
  initial: {
    supplyMode: string | null | undefined;
    sendMode: string | null | undefined;
    sendTime: string | null | undefined;
    /** The Standard lead time (days), read only here: the Lead times card edits it. */
    leadTimeDays: number | null | undefined;
    /** BLU-1526: the Follow stock cutoff; null = off. */
    followStockBelow?: number | null;
  };
  canEdit: boolean;
}

interface Draft {
  supplyMode: SupplyMode;
  sendMode: SendMode;
  /** HH:MM; '' while the time box is empty or half typed. */
  sendTime: string | null;
}

interface FollowDraft {
  on: boolean;
  /** The cutoff as typed. */
  cutoff: string;
}

export function SupplierSupplyCard({
  supplierId,
  supplierName,
  catalogSupplierId = null,
  initial,
  canEdit,
}: SupplierSupplyCardProps) {
  const { t } = useTranslation('inventory');
  const locale = useDateFnsLocale();
  const utils = trpc.useUtils();

  // BLU-1526: the Follow stock row shows only for a linked catalog whose stock
  // PrintRun reads. The catalog list is already cached for the Catalog tab.
  const catalogSuppliers = useCatalogSuppliers(!!catalogSupplierId);
  const followedCatalog = catalogSupplierId
    ? (catalogSuppliers.data ?? []).find((row) => row.id === catalogSupplierId && STOCK_FOLLOWED_CATALOGS.has(row.slug))
    : undefined;
  const showFollow = !!followedCatalog;
  const catalogName = followedCatalog?.name ?? supplierName;
  // Not hidden while it is unknown: a placeholder while the list loads, an error with Retry if it failed.
  const followLoading = !!catalogSupplierId && catalogSuppliers.isLoading;
  const followLoadFailed = !!catalogSupplierId && catalogSuppliers.isError && !catalogSuppliers.data;

  const savedFollowValue = typeof initial.followStockBelow === 'number' ? initial.followStockBelow : null;
  const savedFollow = useMemo<FollowDraft>(
    () => ({ on: savedFollowValue !== null, cutoff: String(savedFollowValue ?? DEFAULT_FOLLOW_CUTOFF) }),
    [savedFollowValue],
  );
  const [followDraft, setFollowDraft] = useState<FollowDraft>(savedFollow);
  // An empty cutoff is only called out once the box is left or Enter is pressed.
  const [cutoffTouched, setCutoffTouched] = useState(false);
  useEffect(() => {
    setFollowDraft(savedFollow);
    setCutoffTouched(false);
  }, [savedFollow]);

  const saved = useMemo<Draft>(
    () => ({
      supplyMode: asSupplyMode(initial.supplyMode),
      sendMode: asSendMode(initial.sendMode),
      sendTime: initial.sendTime ? initial.sendTime.slice(0, 5) : null,
    }),
    [initial.supplyMode, initial.sendMode, initial.sendTime],
  );
  const [draft, setDraft] = useState<Draft>(saved);
  const [justSaved, setJustSaved] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // A save (or another tab's) lands here as a fresh `saved`.
  useEffect(() => {
    setDraft(saved);
  }, [saved]);

  const leadDays = initial.leadTimeDays ?? null;
  const orderAndWait = draft.supplyMode === 'order_and_wait';
  const daily = draft.sendMode === 'daily';
  const effectiveSendTime = daily ? draft.sendTime ?? DEFAULT_SEND_TIME : draft.sendTime;
  // Only a daily Order and wait send needs a time; nothing else is checked.
  const sendTimeOk = !orderAndWait || !daily || HH_MM.test(effectiveSendTime ?? '');

  const patch: {
    supplyMode?: SupplyMode;
    sendMode?: SendMode;
    sendTime?: string | null;
  } = {};
  if (draft.supplyMode !== saved.supplyMode) patch.supplyMode = draft.supplyMode;
  if (orderAndWait) {
    if (draft.sendMode !== saved.sendMode) patch.sendMode = draft.sendMode;
    if (daily && sendTimeOk && effectiveSendTime !== saved.sendTime) patch.sendTime = effectiveSendTime;
  }
  const supplyDirty = Object.keys(patch).length > 0;
  const switchingBack = saved.supplyMode === 'order_and_wait' && draft.supplyMode === 'buy_on_print_day';

  // BLU-1526: the Follow stock draft. On needs a valid cutoff (an empty box is
  // never saved as 0); off saves null.
  const followCutoff = parseFollowCutoff(followDraft.cutoff);
  const followOk = !showFollow || !followDraft.on || followCutoff !== null;
  const followValue = followDraft.on ? followCutoff : null;
  const followDirty = showFollow && followOk && followValue !== savedFollowValue;
  // Changed at all (an invalid cutoff included): Discard shows for it.
  const followTouched =
    showFollow && (followDraft.on !== savedFollow.on || (followDraft.on && followDraft.cutoff !== savedFollow.cutoff));
  const dirty = supplyDirty || followDirty;
  const touched = supplyDirty || followTouched;

  const setSupply = trpc.suppliers.setSupply.useMutation({
    onSuccess: () =>
      Promise.all([
        utils.suppliers.list.invalidate(),
        utils.purchaseOrders.orderAndWait.invalidate(),
        utils.productionOrders.getWaitingOnBlank.invalidate(),
      ]),
  });
  const setFollowStock = trpc.suppliers.setFollowStock.useMutation({
    onSuccess: () =>
      Promise.all([utils.suppliers.list.invalidate(), utils.suppliers.previewFollowStock.invalidate()]),
  });
  const pending = setSupply.isPending || setFollowStock.isPending;

  // How many blanks the typed cutoff closes now: counted only while the
  // switch is on in the draft, for an admin, after typing pauses.
  const previewCutoff = useDebouncedValue(followDraft.on ? followCutoff : null, PREVIEW_DEBOUNCE_MS);
  const preview = trpc.suppliers.previewFollowStock.useQuery(
    { supplierId, cutoff: previewCutoff ?? DEFAULT_FOLLOW_CUTOFF },
    {
      enabled: canEdit && showFollow && followDraft.on && previewCutoff !== null,
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: false,
      placeholderData: (previous) => previous,
    },
  );

  const update = (next: Partial<Draft>) => {
    setJustSaved(false);
    setDraft((current) => ({ ...current, ...next }));
  };
  const updateFollow = (next: Partial<FollowDraft>) => {
    setJustSaved(false);
    setCutoffTouched(false);
    setFollowDraft((current) => ({ ...current, ...next }));
  };

  // Resolves true once the When to buy part is written. The switch-back confirm
  // closes on that alone: a failed follow save reports itself in its own toast.
  const save = async (): Promise<boolean> => {
    if (!dirty || pending || !sendTimeOk || !followOk) return false;
    if (supplyDirty) {
      try {
        const result = await setSupply.mutateAsync({ supplierId, ...patch });
        if (result.switchedBack) {
          toast({
            title: t('whenToBuy.toast.switchedBack', { supplier: supplierName }),
            description:
              result.releasedOrderIds.length > 0
                ? t('whenToBuy.toast.released', { count: result.releasedOrderIds.length })
                : t('whenToBuy.toast.nothingWaiting'),
          });
        }
      } catch (error) {
        toast({
          title: t('whenToBuy.toast.failed'),
          description: supplierSupplyErrorText(t, error, supplierName),
          variant: 'destructive',
        });
        return false;
      }
    }
    if (followDirty) {
      try {
        await setFollowStock.mutateAsync({ supplierId, followStockBelow: followValue });
      } catch (error) {
        toast({
          title: t('whenToBuy.follow.failed', { supplier: catalogName }),
          description: supplierSupplyErrorText(t, error, supplierName),
          variant: 'destructive',
        });
        return supplyDirty;
      }
    }
    setJustSaved(true);
    return true;
  };

  const onSave = () => {
    if (switchingBack) setConfirmOpen(true);
    else void save();
  };

  const radioName = `when-to-buy-${supplierId}`;
  const sendModeId = `when-to-buy-send-${supplierId}`;
  const sendTimeId = `when-to-buy-time-${supplierId}`;
  const sendTimeErrorId = `${sendTimeId}-error`;
  const followId = `follow-stock-${supplierId}`;
  const followHelpId = `${followId}-help`;
  const followCutoffId = `${followId}-cutoff`;
  const followCutoffErrorId = `${followCutoffId}-error`;
  const followCutoffSuffixId = `${followCutoffId}-suffix`;
  // A value that can never be saved is called out at once; an empty box only after blur or Enter.
  const showCutoffError = followCutoff === null && (followDraft.cutoff.trim() !== '' || cutoffTouched);

  // The count line under the cutoff: how many blanks the saved switch shows 0
  // for, or how many saving this draft would.
  const previewText = (() => {
    if (!followDraft.on || followCutoff === null) return null;
    if (preview.isError) return { tone: 'error' as const, retry: true, text: t('whenToBuy.follow.previewFailed') };
    if (!preview.data || preview.isPlaceholderData || previewCutoff !== followCutoff) {
      return { tone: 'muted' as const, retry: false, text: t('whenToBuy.follow.counting') };
    }
    const count = preview.data.closesNow;
    return {
      tone: 'muted' as const,
      retry: false,
      text:
        savedFollowValue !== null && savedFollowValue === followCutoff
          ? t('whenToBuy.follow.closedNow', { count })
          : t('whenToBuy.follow.closesNow', { count }),
    };
  })();

  const sendModeText =
    daily && effectiveSendTime && HH_MM.test(effectiveSendTime)
      ? t('whenToBuy.sendDailyAt', { time: formatSendTime(effectiveSendTime, locale) })
      : t('whenToBuy.sendWhenPressed');

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg">{t('whenToBuy.title')}</CardTitle>
        <p className="text-sm text-muted-foreground">{t('whenToBuy.help', { supplier: supplierName })}</p>
      </CardHeader>
      <CardContent className="space-y-4">
        {canEdit ? (
          <div role="radiogroup" aria-label={t('whenToBuy.title')} className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <OptionCard
              name={radioName}
              value="order_and_wait"
              checked={draft.supplyMode === 'order_and_wait'}
              onChange={() => update({ supplyMode: 'order_and_wait' })}
              label={t('whenToBuy.orderAndWait')}
              body={t('whenToBuy.orderAndWaitBody')}
              disabled={pending}
            />
            <OptionCard
              name={radioName}
              value="buy_on_print_day"
              checked={draft.supplyMode === 'buy_on_print_day'}
              onChange={() => update({ supplyMode: 'buy_on_print_day' })}
              label={t('whenToBuy.buyOnPrintDay')}
              body={t('whenToBuy.buyOnPrintDayBody')}
              disabled={pending}
            />
          </div>
        ) : (
          // Read only: the choice in words, not two disabled cards to decode.
          <div className="text-sm" data-testid="when-to-buy-mode">
            <p className="font-medium">
              {orderAndWait ? t('whenToBuy.orderAndWait') : t('whenToBuy.buyOnPrintDay')}
            </p>
            <p className="text-muted-foreground">
              {orderAndWait ? t('whenToBuy.orderAndWaitBody') : t('whenToBuy.buyOnPrintDayBody')}
            </p>
          </div>
        )}

        {orderAndWait && (
          <dl className="grid grid-cols-1 gap-x-4 gap-y-3 text-sm sm:grid-cols-[minmax(0,10rem)_minmax(0,1fr)] sm:items-center">
            <dt>
              {canEdit ? (
                <label htmlFor={sendModeId} className="text-muted-foreground">
                  {t('whenToBuy.sendLabel')}
                </label>
              ) : (
                <span className="text-muted-foreground">{t('whenToBuy.sendLabel')}</span>
              )}
            </dt>
            {canEdit ? (
              <dd className="flex flex-wrap items-start gap-2">
                <Select
                  value={draft.sendMode}
                  onValueChange={(value) =>
                    update({
                      sendMode: asSendMode(value),
                      sendTime: value === 'daily' ? draft.sendTime ?? DEFAULT_SEND_TIME : draft.sendTime,
                    })
                  }
                  disabled={pending}
                >
                  <SelectTrigger id={sendModeId} className="h-10 w-full sm:h-8 sm:w-56">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">{t('whenToBuy.sendDaily')}</SelectItem>
                    <SelectItem value="when_pressed">{t('whenToBuy.sendWhenPressed')}</SelectItem>
                  </SelectContent>
                </Select>
                {daily && (
                  <div>
                    <label htmlFor={sendTimeId} className="sr-only">
                      {t('whenToBuy.sendTimeLabel')}
                    </label>
                    <Input
                      id={sendTimeId}
                      type="time"
                      step={60}
                      required
                      value={effectiveSendTime ?? ''}
                      onChange={(event) => update({ sendTime: event.target.value })}
                      disabled={pending}
                      aria-invalid={!sendTimeOk || undefined}
                      aria-describedby={!sendTimeOk ? sendTimeErrorId : undefined}
                      className={cn('h-10 w-full tabular-nums sm:h-8 sm:w-32', !sendTimeOk && 'border-destructive')}
                    />
                    {!sendTimeOk && (
                      <div id={sendTimeErrorId} role="alert" className="mt-1 text-xs text-destructive">
                        {t('whenToBuy.sendTimeInvalid')}
                      </div>
                    )}
                  </div>
                )}
              </dd>
            ) : (
              <dd>{sendModeText}</dd>
            )}
            {daily && (
              <dd className="sm:col-start-2">
                <NoteLine icon={Clock}>{t('whenToBuy.sendDailyNote')}</NoteLine>
              </dd>
            )}

            <dt className="text-muted-foreground">{t('whenToBuy.howLabel')}</dt>
            <dd>
              <p>{t('whenToBuy.howWebsite')}</p>
              <NoteLine as="p" className="mt-1">
                {t('whenToBuy.comingLater', { supplier: supplierName })}
              </NoteLine>
            </dd>

            <dt className="text-muted-foreground">{t('whenToBuy.arrivesLabel')}</dt>
            <dd>
              <p className="tabular-nums">
                {leadDays === null ? (
                  <span className="text-muted-foreground">{t('whenToBuy.leadNotSet')}</span>
                ) : (
                  t('whenToBuy.leadDays', { count: leadDays })
                )}
              </p>
              <NoteLine as="p" className="mt-1">
                {leadDays === null ? t('whenToBuy.leadEmpty') : t('whenToBuy.leadNote')}
              </NoteLine>
            </dd>
          </dl>
        )}

        {followLoading && (
          <div
            className="flex items-start justify-between gap-4 border-t pt-4"
            role="status"
            aria-label={t('whenToBuy.follow.loading')}
            data-testid="follow-supplier-stock-loading"
          >
            <div className="min-w-0 space-y-1">
              <Skeleton className="h-5 w-44" />
              <Skeleton className="h-5 w-64 max-w-full" />
            </div>
            <Skeleton className="mt-0.5 h-6 w-11 shrink-0 rounded-full" />
          </div>
        )}

        {followLoadFailed && (
          <div className="border-t pt-4">
            <SectionError
              variant="compact"
              title={t('whenToBuy.follow.loadFailed')}
              onRetry={() => void catalogSuppliers.refetch()}
              retryLabel={t('whenToBuy.follow.retry')}
              retrying={catalogSuppliers.isFetching}
              data-testid="follow-supplier-stock-load-error"
            />
          </div>
        )}

        {showFollow && (
          <div className="space-y-2 border-t pt-4" data-testid="follow-supplier-stock">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                {canEdit ? (
                  <label htmlFor={followId} className="text-sm font-medium">
                    {t('whenToBuy.follow.title', { supplier: catalogName })}
                  </label>
                ) : (
                  <p className="text-sm font-medium">{t('whenToBuy.follow.title', { supplier: catalogName })}</p>
                )}
                <p id={followHelpId} className="text-sm text-muted-foreground">
                  {t('whenToBuy.follow.help')}
                </p>
              </div>
              {canEdit ? (
                <Switch
                  id={followId}
                  checked={followDraft.on}
                  onCheckedChange={(on) => updateFollow({ on })}
                  disabled={pending}
                  aria-describedby={followHelpId}
                  className="mt-0.5"
                />
              ) : (
                <span className="shrink-0 text-sm" data-testid="follow-supplier-stock-state">
                  {savedFollowValue === null ? t('whenToBuy.follow.off') : t('whenToBuy.follow.on')}
                </span>
              )}
            </div>
            {canEdit && followDraft.on && (
              <div className="space-y-1">
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <label htmlFor={followCutoffId} className="text-muted-foreground">
                    {t('whenToBuy.follow.cutoffLabel', { supplier: catalogName })}
                  </label>
                  <Input
                    id={followCutoffId}
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    value={followDraft.cutoff}
                    onChange={(event) => updateFollow({ cutoff: event.target.value })}
                    onBlur={() => setCutoffTouched(true)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') setCutoffTouched(true);
                    }}
                    disabled={pending}
                    aria-invalid={showCutoffError || undefined}
                    aria-describedby={
                      showCutoffError ? `${followCutoffSuffixId} ${followCutoffErrorId}` : followCutoffSuffixId
                    }
                    className={cn('h-10 w-24 tabular-nums sm:h-8', showCutoffError && 'border-destructive')}
                  />
                  <span id={followCutoffSuffixId} className="text-muted-foreground">
                    {t('whenToBuy.follow.cutoffSuffix')}
                  </span>
                </div>
                {showCutoffError ? (
                  <div id={followCutoffErrorId} role="alert" className="text-xs text-destructive">
                    {t('whenToBuy.follow.cutoffInvalid')}
                  </div>
                ) : (
                  previewText && (
                    <div role="status" data-testid="follow-supplier-stock-preview">
                      <NoteLine tone={previewText.tone}>
                        {previewText.text}
                        {previewText.retry && (
                          <>
                            {' '}
                            <button
                              type="button"
                              onClick={() => void preview.refetch()}
                              className="rounded-sm underline underline-offset-2 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            >
                              {t('whenToBuy.follow.retry')}
                            </button>
                          </>
                        )}
                      </NoteLine>
                    </div>
                  )
                )}
              </div>
            )}
            {!canEdit && savedFollowValue !== null && (
              <NoteLine as="p">
                {t('whenToBuy.follow.cutoffSaved', { supplier: catalogName, cutoff: savedFollowValue })}
              </NoteLine>
            )}
          </div>
        )}

        {canEdit ? (
          <div className="flex flex-wrap items-center justify-end gap-2">
            {justSaved && !touched && (
              <span role="status" className="mr-auto text-xs text-emerald-700">
                {t('whenToBuy.saved')}
              </span>
            )}
            {touched && (
              <Button
                type="button"
                variant="outline"
                className="h-8 px-3 text-xs"
                onClick={() => {
                  setJustSaved(false);
                  setDraft(saved);
                  setFollowDraft(savedFollow);
                  setCutoffTouched(false);
                }}
                disabled={pending}
              >
                {t('whenToBuy.discard')}
              </Button>
            )}
            <Button
              type="button"
              className="h-8 gap-1.5 bg-threadheads-purple px-3 text-xs text-white hover:bg-threadheads-purple/90"
              onClick={onSave}
              disabled={!dirty || pending || !sendTimeOk || !followOk}
              aria-busy={pending || undefined}
            >
              {pending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" aria-hidden="true" />
              ) : (
                <Save className="h-3.5 w-3.5" aria-hidden="true" />
              )}
              {t('whenToBuy.save')}
            </Button>
          </div>
        ) : (
          <NoteLine as="p">{t('whenToBuy.adminOnly')}</NoteLine>
        )}
      </CardContent>

      <AlertDialog open={confirmOpen} onOpenChange={(next) => (pending ? undefined : setConfirmOpen(next))}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('whenToBuy.confirm.title', { supplier: supplierName })}</AlertDialogTitle>
            <AlertDialogDescription>{t('whenToBuy.confirm.body', { supplier: supplierName })}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={pending}>{t('whenToBuy.confirm.back')}</AlertDialogCancel>
            <Button
              type="button"
              variant="outline"
              onClick={async () => {
                if (await save()) setConfirmOpen(false);
              }}
              disabled={pending}
              aria-busy={pending || undefined}
              className="gap-1.5 border-orange-300 text-orange-600 hover:bg-orange-50 hover:text-orange-700"
            >
              {pending && <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />}
              {t('whenToBuy.confirm.switch')}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
